import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, FileText, Loader2 } from "lucide-react";

export default function Manuals() {
  const navigate = useNavigate();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/manuals").then((r) => setManuals(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const createManual = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/manuals", {
        title: "Nieuwe handleiding",
        product_name: "",
        intro: "",
        steps: [{ title: "", description: "", image: null }],
        warnings: "",
        contact: "KANN\nsupport@kann.store\nwww.kann.store",
      });
      navigate(`/manuals/${data.id}`);
    } catch { toast.error("Kon handleiding niet aanmaken"); }
    finally { setCreating(false); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/manuals/${id}`);
    setManuals((m) => m.filter((x) => x.id !== id));
    toast.success("Verwijderd");
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-16 md:pt-10 relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 620, height: 620, top: -140, left: "55%" }} /></div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">Product documentatie</div>
            <h1 className="text-3xl sm:text-4xl font-medium text-kann-text-pri mt-2">Handleidingen</h1>
            <p className="font-light text-sm md:text-base text-kann-text-sec mt-2">Maak on-brand installatiehandleidingen en download ze als PDF.</p>
          </div>
          <button onClick={createManual} disabled={creating} data-testid="new-manual-btn"
            className="w-full sm:w-auto justify-center bg-kann-ink text-white font-semibold rounded-full px-5 py-3 text-sm hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-60 shrink-0">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Nieuwe handleiding
          </button>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="py-20 text-center text-kann-text-sec"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : manuals.length === 0 ? (
            <div className="border border-dashed border-kann-border/40 rounded-lg py-20 text-center">
              <BookOpen className="w-8 h-8 text-kann-text-sec mx-auto mb-3" />
              <p className="text-kann-text-sec">Nog geen handleidingen — maak je eerste hierboven.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="manuals-grid">
              {manuals.map((m) => (
                <div key={m.id} onClick={() => navigate(`/manuals/${m.id}`)} data-testid={`manual-card-${m.id}`}
                  className="group relative bg-kann-surface border border-kann-border/30 rounded-lg p-5 cursor-pointer hover:border-kann-gold transition-colors">
                  <div className="w-11 h-11 rounded-lg bg-kann-ink flex items-center justify-center mb-4"><FileText className="w-5 h-5 text-white" /></div>
                  <div className="text-base font-medium text-kann-text-pri truncate">{m.title || "Naamloze handleiding"}</div>
                  <div className="text-sm text-kann-text-sec truncate mt-0.5">{m.product_name || "Geen product"}</div>
                  <div className="text-xs text-kann-text-sec mt-3">{((m.steps?.length || 0) + (m.sections || []).reduce((n, s) => n + (s.steps?.length || 0), 0))} stappen · {fmt(m.updated_at)}</div>
                  <button onClick={(e) => remove(e, m.id)} data-testid={`delete-manual-${m.id}`}
                    className="absolute top-3 right-3 p-2 rounded-md text-kann-text-sec opacity-0 group-hover:opacity-100 hover:bg-red-500/10 hover:text-red-500 transition-all"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
